import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { scheduleAppointment, getDoctors, getDoctor, getPatient } from "@/lib/api";
import { AppointmentDialogContent, DayPicker, Field, TimeSlots, dayLabel, timeLabel } from "@/components/Appointment/AppointmentFields";
import PatientSearchInput from "@/components/shared/PatientSearchInput";
import type { PatientSearchResult } from "@/hooks/usePatientSearch";
import { Doctor } from "@/types/doctor";
import { useAuth } from "@/context/AuthContext";
import { displayAge } from "@/lib/patientAge";

interface Patient {
  id: string; name: string; age: number; gender: string; phone: string; email?: string; date_of_birth?: string;
}

interface AddAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId?: string;
  patients?: Patient[];
  onAdded?: () => void;
  onPatientSelected?: (patient: PatientSearchResult) => void;
  /** Called with what was typed in the patient search, to prefill the new-patient form */
  onCreateNewPatient?: (searchTerm: string) => void;
  showPatientSelection?: boolean;
  title?: string;
  initialDate?: string;
  initialTime?: string;
  /** Staff booking for a patient already with a doctor (e.g. from the queue): start on that doctor */
  initialDoctorId?: string;
}

interface AppointmentForm {
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  time: string;
  reason: string;
  status?: string;
}

export default function AddAppointmentModal({
  isOpen, onClose,
  patientId = '', patients = [],
  onAdded, onPatientSelected, onCreateNewPatient,
  showPatientSelection = true,
  title = "Book appointment",
  initialDate, initialTime, initialDoctorId,
}: Readonly<AddAppointmentModalProps>) {
  const { currentUser } = useAuth();

  const [form, setForm] = useState<AppointmentForm>({
    patient_id: patientId, doctor_id: '', clinic_id: '',
    date: format(new Date(), "yyyy-MM-dd"), time: '', reason: ''
  });

  const [loading, setLoading]         = useState(false);
  const [doctors, setDoctors]         = useState<Doctor[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [pickedPatient, setPickedPatient]       = useState<PatientSearchResult | null>(null);
  const [patientSearchText, setPatientSearchText] = useState('');
  // A patient given by id only (e.g. "Book follow-up" from billing): loaded so the form says who it's for
  const [fetchedPatient, setFetchedPatient]     = useState<Patient | null>(null);
  // What's missing is shown under its field once Book was pressed, not before
  const [tried, setTried]                       = useState(false);

  const isDoctor = !!currentUser?.doctor_id && !!currentUser?.clinic_id;
  const isStaff  = !isDoctor && !!currentUser?.clinic_id;

  const doctorsInClinic = useMemo(
    () => (form.clinic_id ? doctors.filter(d => d.clinic_id === form.clinic_id) : doctors),
    [doctors, form.clinic_id]
  );
  const doctorDropdownDisabled = isStaff && doctorsInClinic.length === 1;

  useEffect(() => {
    if (patientId !== form.patient_id) setForm(prev => ({ ...prev, patient_id: patientId }));
  }, [patientId]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        patient_id: '', doctor_id: isDoctor ? currentUser?.doctor_id || '' : '',
        clinic_id: (isDoctor || isStaff) ? currentUser?.clinic_id || '' : '',
        date: format(new Date(), "yyyy-MM-dd"), time: "", reason: ""
      });
      setLoading(false); setLoadingData(false);
      setPickedPatient(null); setPatientSearchText(''); setFetchedPatient(null); setTried(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm(prev => ({
        ...prev,
        patient_id: patientId || '',
        doctor_id: isDoctor ? currentUser?.doctor_id || '' : initialDoctorId || prev.doctor_id,
        clinic_id: (isDoctor || isStaff) ? currentUser?.clinic_id || '' : prev.clinic_id,
        date: initialDate || format(new Date(), "yyyy-MM-dd"),
        time: initialTime || "", reason: ""
      }));
    }
  }, [isOpen, patientId]);

  useEffect(() => {
    if (!isOpen) return;
    if (isDoctor && currentUser?.doctor_id && currentUser?.clinic_id) loadDoctorAndClinicForDoctor();
    else if (isStaff && currentUser?.clinic_id) loadDoctorsAndClinicForStaff();
  }, [isOpen, isDoctor, isStaff, currentUser?.doctor_id, currentUser?.clinic_id]);

  const loadDoctorAndClinicForDoctor = async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    setLoadingData(true);
    try {
      const doctorData = await getDoctor(currentUser.doctor_id);
      setDoctors(doctorData ? [doctorData] : []);
      setForm(prev => ({ ...prev, doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id }));
    } catch {
      toast({ title: "Loading Error", description: "Failed to load your clinic info", variant: "destructive" });
    } finally { setLoadingData(false); }
  };

  const loadDoctorsAndClinicForStaff = async () => {
    const clinicId = currentUser?.clinic_id;
    if (!clinicId) return;
    setLoadingData(true);
    try {
      const allDoctors = (await getDoctors() || []).filter((d: Doctor) => d.clinic_id === clinicId);
      setDoctors(allDoctors);
      setForm(prev => {
        const next = { ...prev, clinic_id: clinicId };
        // Chosen for staff only when there's no choice; picking the first of several books the wrong doctor unseen
        if (allDoctors.length === 1 && !prev.doctor_id) next.doctor_id = allDoctors[0].id;
        return next;
      });
    } catch {
      toast({ title: "Loading Error", description: "Failed to load doctors and clinic", variant: "destructive" });
    } finally { setLoadingData(false); }
  };

  const listedPatient = form.patient_id ? patients.find(p => p.id === form.patient_id) ?? null : null;
  useEffect(() => {
    if (!isOpen || !form.patient_id || pickedPatient || listedPatient) return;
    let cancelled = false;
    getPatient(form.patient_id).then(p => { if (!cancelled && p) setFetchedPatient(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, form.patient_id, pickedPatient, listedPatient]);

  const selectedPatient = useMemo(() => {
    if (pickedPatient) return pickedPatient;
    if (!form.patient_id) return null;
    return listedPatient ?? (fetchedPatient?.id === form.patient_id ? fetchedPatient : null);
  }, [pickedPatient, listedPatient, fetchedPatient, form.patient_id]);

  const handlePatientDeselect = () => { setForm(prev => ({ ...prev, patient_id: '' })); setPickedPatient(null); setPatientSearchText(''); };
  const handlePatientPicked   = (patient: PatientSearchResult) => {
    setForm(prev => ({ ...prev, patient_id: patient.id }));
    setPickedPatient(patient); setPatientSearchText(patient.name);
    onPatientSelected?.(patient);
  };

  const errors = {
    patient: !form.patient_id ? 'Choose a patient' : null,
    doctor: !form.doctor_id ? 'Choose a doctor' : null,
    time: !form.time ? 'Choose a time' : null,
  };
  const isFormValid = !errors.patient && !errors.doctor && !errors.time && !!form.clinic_id && !!form.date;

  const handleSubmit = async () => {
    setTried(true);
    if (!isFormValid || loading) return;
    setLoading(true);
    try {
      await scheduleAppointment({
        patient_id: form.patient_id, doctor_id: form.doctor_id, clinic_id: form.clinic_id,
        date: form.date, time: form.time, reason: (form.reason || '').trim(), status: 'scheduled'
      });
      toast({
        title: "Appointment booked",
        description: [selectedPatient?.name, `${dayLabel(form.date)}, ${timeLabel(form.time)}`].filter(Boolean).join(' · '),
      });
      onAdded?.();
    } catch (err: any) {
      toast({ title: "Couldn't book the appointment", description: err.message || "Try again in a moment.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const patientLine = (p: { name: string; age?: number; date_of_birth?: string; gender?: string; phone?: string; mrn?: string }) => (
    <div className="flex items-center gap-3 min-h-9 rounded-md border border-border bg-muted/40 px-3 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground capitalize truncate">{p.name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {[displayAge(p.age, p.date_of_birth), p.gender, p.phone, p.mrn].filter(Boolean).join(' · ')}
        </p>
      </div>
      {showPatientSelection && (
        <button type="button" onClick={handlePatientDeselect} disabled={loading}
          className="shrink-0 text-[13px] font-medium text-primary hover:underline underline-offset-2">
          Change
        </button>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <AppointmentDialogContent
        title={title}
        onClose={onClose}
        onPointerDownOutside={e => {
          if ((e.target as HTMLElement)?.closest('[data-patient-search-dropdown]')) e.preventDefault();
        }}
        footer={<>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="book-appointment" disabled={loading || loadingData} className="gap-1.5">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</> : 'Book appointment'}
          </Button>
        </>}
      >
        {loadingData ? (
          <div className="py-8 flex items-center justify-center gap-2.5">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Loading…</p>
          </div>
        ) : (
          <form id="book-appointment" onSubmit={e => { e.preventDefault(); handleSubmit(); }} className="space-y-4">

            {/* Patient: search, or the chosen patient in one line */}
            <Field label="Patient" error={tried ? errors.patient : null}>
              {selectedPatient ? patientLine(selectedPatient) : form.patient_id ? (
                <div className="min-h-9 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">Loading patient…</div>
              ) : (
                <PatientSearchInput
                  value={patientSearchText} onChange={setPatientSearchText}
                  onSelect={handlePatientPicked}
                  allowCreateNew={!!onCreateNewPatient} onCreateNew={term => onCreateNewPatient?.(term)}
                  placeholder="Name, phone or MRN"
                  inputSize="sm" className="h-9 px-3"
                />
              )}
            </Field>

            {/* Doctor: staff only, and only when the clinic has a choice */}
            {!isDoctor && !doctorDropdownDisabled && (
              <Field label="Doctor" error={tried ? errors.doctor : null}>
                <Select value={form.doctor_id} onValueChange={v => setForm(prev => ({ ...prev, doctor_id: v }))} disabled={loading}>
                  <SelectTrigger aria-label="Doctor"><SelectValue placeholder="Choose a doctor" /></SelectTrigger>
                  <SelectContent>
                    {(doctorsInClinic.length ? doctorsInClinic : doctors).map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field label="Day">
              <DayPicker value={form.date} onChange={date => setForm(prev => ({ ...prev, date }))} disabled={loading} />
            </Field>

            <Field label="Time" error={tried ? errors.time : null}>
              <TimeSlots
                day={form.date} value={form.time}
                onChange={time => setForm(prev => ({ ...prev, time }))}
                doctorId={form.doctor_id} clinicId={form.clinic_id} disabled={loading}
              />
            </Field>

            <Field label="Reason" htmlFor="appointment-reason">
              <Input
                id="appointment-reason" value={form.reason}
                onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Fever for 3 days" disabled={loading} className="h-9 text-sm"
              />
            </Field>
          </form>
        )}
      </AppointmentDialogContent>
    </Dialog>
  );
}
